import type { DesktopAccountSummary } from '@/lib/utils/tauri'
import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout'
// Import shadcn components
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GoogleIcon } from '@/components/ui/google-icon'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MicrosoftIcon } from '@/components/ui/microsoft-icon'
import { UserAvatar } from '@/components/ui/user-avatar'
import { authApi } from '@/lib/api/auth.api'
import { dismissBootSplash } from '@/lib/boot-splash'
import { organizationInvitesApi } from '@/lib/api/organization-invites.api'
import { createLogger } from '@/lib/logger'
import { AppLoader } from '@/lib/router/components'
import {
  desktopAuthFlow,
  desktopSecureStorage,
  inAppOAuthFlow,
  isTauri,
  setupDeepLinkAuthListener,
} from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
import { useLoginSession } from '@/store/login-session.store'
import {
  clearAccountLinkRetryFlags,
  isAccountLinkingRetryError,
} from '@/utils/auth/account-linking'
import {
  getAdminReturnTarget,
  persistAdminReturnTarget,
  redirectToAdminReturnTarget,
} from '@/utils/auth/admin-handoff'
import { getAuthRedirectTarget } from '@/utils/auth/redirects'
import {
  isMobileAuthFlow,
  redirectToMobileApp,
  storeMobileAuthContext,
} from '@/utils/mobile-auth'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'sonner'

const logger = createLogger('pages/login/index')

// Note: Account selection prompt is now handled by the backend via forceNewAccount parameter
// Helper function to show signup toast
function showSignUpToast(email: string, isAddAccount?: boolean) {
  const registerParams = new URLSearchParams()
  registerParams.set('email', email)
  if (isAddAccount) {
    registerParams.set('addAccount', 'true')
  }
  const registerUrl = `/register?${registerParams.toString()}`

  toast(
    <div className="flex flex-col space-y-2">
      <p className="font-medium text-foreground">Account not found</p>
      <p className="text-sm text-muted-foreground">
        We couldn&apos;t find an account with{' '}
        <span className="font-medium text-foreground">{email}</span>
      </p>
      <div className="mt-1 flex flex-col space-y-2">
        <Button
          onClick={() => {
            // Redirect to register page with email prefilled
            window.location.href = registerUrl
          }}
          className="w-full"
        >
          Create Account
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            toast.dismiss('signup-toast')
          }}
          className="w-full border-border text-foreground hover:bg-muted"
        >
          Try Another Email
        </Button>
      </div>
    </div>,
    {
      duration: 10000,
      id: 'signup-toast',
      position: 'top-center',
      className: 'signup-toast-wrapper',
    },
  )
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export default function LoginPage() {
  // Unauthenticated desktop boots land here — release the boot splash.
  useEffect(() => {
    dismissBootSplash()
  }, [])

  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authMode, setAuthMode] = useState<'code' | 'password'>('code')
  const [showEmailAuth, setShowEmailAuth] = useState(() =>
    Boolean(
      searchParams.get('email') ||
      searchParams.get('token') ||
      searchParams.get('adminReturn') ||
      searchParams.has('addAccount') ||
      searchParams.get('forceNewAccount') === 'true',
    ),
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false)
  const [desktopAccounts, setDesktopAccounts] = useState<
    DesktopAccountSummary[]
  >([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [selectedDesktopAccountId, setSelectedDesktopAccountId] = useState<
    string | null
  >(null)
  const [desktopAccountError, setDesktopAccountError] = useState<{
    accountId: string
    email?: string
    message: string
  } | null>(null)
  const visibleDesktopAccounts = desktopAccounts.filter(account =>
    Boolean(account.email),
  )
  const { isAuthenticated, isLoading: authLoading, checkUserAuth } = useAuth()
  const inviteToken = searchParams.get('token')
  const redirectUrl =
    searchParams.get('redirect') || searchParams.get('returnUrl') // Support both parameters
  const adminReturnTarget = getAdminReturnTarget(location.search)
  // Check for both addAccount (web) and forceNewAccount (mobile) params
  const isAddAccountFlow =
    searchParams.has('addAccount') ||
    searchParams.get('forceNewAccount') === 'true'
  const [inviteInfo, setInviteInfo] = useState<{
    organizationName?: string
    email?: string
  } | null>(null)
  const [isLoadingInvite, setIsLoadingInvite] = useState(false)

  // Add refs to track if we've already applied the email
  const emailFromUrlApplied = useRef(false)
  const emailFromInviteApplied = useRef(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  // Get the setLoginSession action from our store
  const setLoginSession = useLoginSession(state => state.setLoginSession)
  const isConfirmationFlow = useLoginSession(state => state.isConfirmationFlow)
  const clearSession = useLoginSession(state => state.clearSession)

  // Add a ref to prevent multiple clearing operations
  const clearSessionExecuted = useRef(false)
  const hasRedirected = useRef(false)

  // Pre-fill email from URL parameters or invite info
  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam && !emailFromUrlApplied.current) {
      emailFromUrlApplied.current = true
      setEmail(normalizeEmail(emailParam))
      setShowEmailAuth(true)
    } else if (inviteInfo?.email && !emailFromInviteApplied.current) {
      emailFromInviteApplied.current = true
      setEmail(normalizeEmail(inviteInfo.email))
      setShowEmailAuth(true)
    }
  }, [searchParams, inviteInfo?.email])

  useEffect(() => {
    if (!showEmailAuth) return

    const focusTimer = window.setTimeout(() => {
      emailInputRef.current?.focus()
    }, 0)

    return () => window.clearTimeout(focusTimer)
  }, [showEmailAuth])

  // Check for invite token and fetch invite info
  useEffect(() => {
    if (inviteToken && !isLoadingInvite && !inviteInfo) {
      const fetchInviteInfo = async () => {
        setIsLoadingInvite(true)
        try {
          // Using the new API endpoint to fetch invite info
          const inviteData =
            await organizationInvitesApi.getByToken(inviteToken)

          if (inviteData) {
            setInviteInfo(inviteData)

            // Set the email from invite data if available
            if (inviteData.email && !email) {
              setEmail(normalizeEmail(inviteData.email))
              emailFromInviteApplied.current = true
            }
          }
        } catch (error) {
          logger.error('[Login] Error fetching invite info:', error)
        } finally {
          setIsLoadingInvite(false)
        }
      }

      fetchInviteInfo()
    }
  }, [inviteToken, isLoadingInvite, inviteInfo, email])

  // Store invite token in localStorage for later use during confirmation
  useEffect(() => {
    if (inviteToken) {
      localStorage.setItem('pendingInviteToken', inviteToken)
    }
  }, [inviteToken])

  // Store mobile auth context if platform=mobile is in URL params
  useEffect(() => {
    const platform = searchParams.get('platform')
    const mobileRedirect = searchParams.get('redirect')
    storeMobileAuthContext(platform, mobileRedirect)
  }, [searchParams])

  // Set up deep link listener for desktop auth flow
  useEffect(() => {
    if (isTauri()) {
      setupDeepLinkAuthListener()
    }
  }, [])

  // Add an effect to clear any existing session data on page load
  useEffect(() => {
    // Only clear session data if we're not in the middle of a login flow
    if (!isLoading && !isConfirmationFlow) {
      // Use a ref to prevent multiple clearing operations
      if (!clearSessionExecuted.current) {
        clearSession()
        clearSessionExecuted.current = true
      }
    }
  }, [isLoading, isConfirmationFlow, clearSession])

  useEffect(() => {
    // Check for error in URL
    const error = searchParams.get('error')
    if (error) {
      toast.error('Authentication failed')
    }
  }, [searchParams])

  useEffect(() => {
    // If authenticated and not loading, redirect (unless this is mobile auth flow)
    // For mobile auth, we still need to complete the flow and redirect with tokens
    if (isAuthenticated && !isMobileAuthFlow() && !isAddAccountFlow) {
      if (hasRedirected.current) return
      void (async () => {
        if (await redirectToAdminReturnTarget(adminReturnTarget)) {
          hasRedirected.current = true
          return
        }

        const redirectTarget = getAuthRedirectTarget(
          location.search,
          location.pathname,
        )
        if (redirectTarget !== location.pathname + location.search) {
          hasRedirected.current = true
          navigate(redirectTarget, { replace: true })
        }
      })()
    }
  }, [
    adminReturnTarget,
    isAuthenticated,
    isAddAccountFlow,
    location.pathname,
    location.search,
    navigate,
  ])

  useEffect(() => {
    if (!isTauri()) return
    let isActive = true
    setIsLoadingAccounts(true)
    desktopSecureStorage
      .getAccounts()
      .then(accounts => {
        if (isActive) {
          setDesktopAccounts(accounts)
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingAccounts(false)
        }
      })
    return () => {
      isActive = false
    }
  }, [])

  const handleSavedAccountSelect = async (account: DesktopAccountSummary) => {
    setSelectedDesktopAccountId(account.id)
    setDesktopAccountError(null)

    const previousAccountId = await desktopSecureStorage.getActiveAccountId()
    const didSwitch = await desktopSecureStorage.setActiveAccount(account.id)
    if (!didSwitch) {
      setDesktopAccountError({
        accountId: account.id,
        email: account.email,
        message:
          'We could not use that saved account. Sign in again to refresh it.',
      })
      setSelectedDesktopAccountId(null)
      return
    }

    try {
      const currentUser = await authApi.getCurrentUser()
      if (currentUser.success && currentUser.data) {
        resetPersistedModuleFilters()
        localStorage.removeItem('currentOrganizationId')
        window.location.href = '/organizations'
        return
      }

      const refreshResponse = await authApi.refreshToken()
      if (refreshResponse.success) {
        const refreshedUser = await authApi.getCurrentUser()
        if (refreshedUser.success && refreshedUser.data) {
          resetPersistedModuleFilters()
          localStorage.removeItem('currentOrganizationId')
          window.location.href = '/organizations'
          return
        }
      }

      if (previousAccountId && previousAccountId !== account.id) {
        await desktopSecureStorage.setActiveAccount(previousAccountId)
      }

      const message =
        refreshResponse.message &&
        refreshResponse.message !== 'Failed to refresh token'
          ? refreshResponse.message
          : 'Your saved session has expired. Sign in again to refresh this account.'

      setDesktopAccountError({
        accountId: account.id,
        email: account.email,
        message,
      })
    } catch (error) {
      logger.error('[Login] Saved desktop account sign-in failed:', error)
      if (previousAccountId && previousAccountId !== account.id) {
        await desktopSecureStorage.setActiveAccount(previousAccountId)
      }
      setDesktopAccountError({
        accountId: account.id,
        email: account.email,
        message:
          'We could not verify that saved account. Sign in again to refresh it.',
      })
    } finally {
      setSelectedDesktopAccountId(null)
    }
  }

  const handleDesktopAccountReauth = (
    account: DesktopAccountSummary | undefined,
  ) => {
    if (account?.email) {
      setEmail(normalizeEmail(account.email))
    }
    setAuthMode('code')
    setShowEmailAuth(true)
    setDesktopAccountError(null)
  }

  const handleDesktopAccountRemove = async (accountId: string) => {
    try {
      await desktopSecureStorage.removeAccount(accountId)
      setDesktopAccounts(accounts =>
        accounts.filter(account => account.id !== accountId),
      )
      setDesktopAccountError(null)
    } catch (error) {
      logger.error('[Login] Failed to remove saved desktop account:', error)
      toast.error('Could not remove that saved account')
    }
  }

  // Add an effect to check if we're in a confirmation flow and redirect if needed
  useEffect(() => {
    if (isConfirmationFlow) {
      const confirmationParams = new URLSearchParams()
      if (redirectUrl) {
        confirmationParams.set('redirect', redirectUrl)
      }
      if (adminReturnTarget) {
        confirmationParams.set('adminReturn', adminReturnTarget)
      }
      if (isAddAccountFlow) {
        confirmationParams.set('addAccount', 'true')
      }
      const confirmationUrl = confirmationParams.toString()
        ? `/login/confirmation?${confirmationParams.toString()}`
        : '/login/confirmation'
      navigate(confirmationUrl)
    }
  }, [
    adminReturnTarget,
    isConfirmationFlow,
    isAddAccountFlow,
    navigate,
    redirectUrl,
  ])

  const handleGoogleSignIn = async (isLinkRetryAttempt?: boolean) => {
    const isLinkRetry = isLinkRetryAttempt === true
    try {
      setIsGoogleLoading(true)

      // Pass 'login' as the flow type to indicate this is login-only (no account creation)
      // For desktop/mobile flows, pass platform so callback handling can return tokens correctly.
      // Pass forceNewAccount to show account picker when adding new account
      const platform = isTauri()
        ? 'desktop'
        : isMobileAuthFlow()
          ? 'mobile'
          : undefined

      logger.info('[GoogleSignIn] Initiating', {
        platform,
        isTauri: isTauri(),
        isMobileAuthFlow: isMobileAuthFlow(),
        isAddAccountFlow,
        hasInviteToken: !!inviteToken,
      })

      const response = await authApi.getGoogleLoginUrl(
        inviteToken || undefined,
        'login',
        platform,
        undefined, // deepLinkScheme - will be auto-detected for desktop
        isAddAccountFlow, // forceNewAccount - shows account picker
      )

      logger.info('[GoogleSignIn] Got response', {
        success: response.success,
        hasAuthUrl: !!(response.data && 'authUrl' in response.data),
        authUrl:
          response.data && 'authUrl' in response.data
            ? response.data.authUrl
            : 'N/A',
      })

      if (response.success && response.data && 'authUrl' in response.data) {
        const authUrl = response.data.authUrl as string

        // Store the redirect URL if present
        if (redirectUrl) {
          localStorage.setItem('authRedirectUrl', redirectUrl)
        }
        persistAdminReturnTarget(location.search)

        // Desktop flow: use in-app OAuth window for local dev, external browser otherwise
        if (isTauri()) {
          try {
            if (import.meta.env.DEV) {
              // Local dev: use in-app OAuth window.
              const { code, state } = await inAppOAuthFlow(
                authUrl,
                '/auth/callback',
                300000,
              )
              await authApi.handleGoogleCallback(code, state || '', 'desktop')
            } else {
              // Production: use external browser + deep link flow
              await desktopAuthFlow(authUrl, 300000, {
                forceNewAccount: isAddAccountFlow,
              })
            }
            clearAccountLinkRetryFlags()
            await checkUserAuth({ force: true })

            if (await redirectToAdminReturnTarget(adminReturnTarget)) {
              return
            }

            const targetUrl = redirectUrl
              ? decodeURIComponent(redirectUrl)
              : adminReturnTarget
                ? '/organizations'
                : '/today'
            navigate(targetUrl)
          } catch (error) {
            // First-time federated sign-in: the Pre-SignUp trigger linked the
            // identity to the native user and aborted this attempt. Retry the
            // same sign-in ONCE — the linked sign-in then succeeds.
            if (
              !isLinkRetry &&
              isAccountLinkingRetryError(
                error instanceof Error ? error.message : String(error),
              )
            ) {
              logger.info(
                '[Login] Account linked mid Google sign-in — retrying once',
              )
              await handleGoogleSignIn(true)
              return
            }
            logger.error('[Login] Desktop auth flow failed:', error)
            toast.error(
              error instanceof Error ? error.message : 'Authentication failed',
            )
            setIsGoogleLoading(false)
          }
          return
        }

        // Add a small delay to ensure the loading state is visible
        setTimeout(() => {
          window.location.href = authUrl
        }, 100)
      } else {
        toast.error('Failed to initiate Google sign-in')
        setIsGoogleLoading(false)
      }
    } catch (error) {
      logger.error('[Login] Google sign-in failed:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'unknown',
        stack: error instanceof Error ? error.stack : undefined,
      })
      toast.error(
        `Failed to connect with Google: ${error instanceof Error ? error.message : String(error)}`,
      )
      setIsGoogleLoading(false)
    }
    // Note: Don't set isLoading to false in finally block since we're redirecting
  }

  const handleMicrosoftSignIn = async (
    accountType: 'personal' | 'work',
    isLinkRetryAttempt?: boolean,
  ) => {
    const isLinkRetry = isLinkRetryAttempt === true
    try {
      setIsMicrosoftLoading(true)

      // Pass 'login' as the flow type to indicate this is login-only (no account creation)
      // For desktop/mobile flows, pass platform so callback handling can return tokens correctly.
      const platform = isTauri()
        ? 'desktop'
        : isMobileAuthFlow()
          ? 'mobile'
          : undefined

      logger.info('[MicrosoftSignIn] Initiating', {
        accountType,
        platform,
        isTauri: isTauri(),
        isMobileAuthFlow: isMobileAuthFlow(),
        hasInviteToken: !!inviteToken,
      })

      const response = await authApi.getMicrosoftLoginUrl(
        accountType,
        inviteToken || undefined,
        'login',
        platform,
        undefined, // deepLinkScheme - will be auto-detected for desktop
      )

      logger.info('[MicrosoftSignIn] Got response', {
        success: response.success,
        hasAuthUrl: !!(response.data && 'authUrl' in response.data),
      })

      if (response.success && response.data && 'authUrl' in response.data) {
        const authUrl = response.data.authUrl as string

        // Store the redirect URL if present
        if (redirectUrl) {
          localStorage.setItem('authRedirectUrl', redirectUrl)
        }
        persistAdminReturnTarget(location.search)

        // Desktop flow: use in-app OAuth window for local dev, external browser otherwise
        if (isTauri()) {
          try {
            if (import.meta.env.DEV) {
              // Local dev: use in-app OAuth window.
              const { code, state } = await inAppOAuthFlow(
                authUrl,
                '/auth/callback',
                300000,
              )
              await authApi.handleMicrosoftCallback(
                code,
                state || '',
                'desktop',
              )
            } else {
              // Production: use external browser + deep link flow
              await desktopAuthFlow(authUrl, 300000, {
                forceNewAccount: isAddAccountFlow,
              })
            }
            clearAccountLinkRetryFlags()
            await checkUserAuth({ force: true })

            if (await redirectToAdminReturnTarget(adminReturnTarget)) {
              return
            }

            const targetUrl = redirectUrl
              ? decodeURIComponent(redirectUrl)
              : adminReturnTarget
                ? '/organizations'
                : '/today'
            navigate(targetUrl)
          } catch (error) {
            // First-time federated sign-in: the Pre-SignUp trigger linked the
            // identity to the native user and aborted this attempt. Retry the
            // same sign-in (same accountType) ONCE.
            if (
              !isLinkRetry &&
              isAccountLinkingRetryError(
                error instanceof Error ? error.message : String(error),
              )
            ) {
              logger.info(
                '[Login] Account linked mid Microsoft sign-in — retrying once',
                { accountType },
              )
              await handleMicrosoftSignIn(accountType, true)
              return
            }
            logger.error('[Login] Desktop Microsoft auth flow failed:', error)
            toast.error(
              error instanceof Error ? error.message : 'Authentication failed',
            )
            setIsMicrosoftLoading(false)
          }
          return
        }

        // Add a small delay to ensure the loading state is visible
        setTimeout(() => {
          window.location.href = authUrl
        }, 100)
      } else {
        toast.error('Failed to initiate Microsoft sign-in')
        setIsMicrosoftLoading(false)
      }
    } catch (error) {
      logger.error('[Login] Microsoft sign-in failed:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'unknown',
      })
      toast.error(
        `Failed to connect with Microsoft: ${error instanceof Error ? error.message : String(error)}`,
      )
      setIsMicrosoftLoading(false)
    }
    // Note: Don't set isMicrosoftLoading to false in finally block since we're redirecting
  }

  const completePasswordLogin = async () => {
    await checkUserAuth({ force: true })

    const pendingInviteToken = localStorage.getItem('pendingInviteToken')
    if (pendingInviteToken) {
      try {
        await organizationInvitesApi.acceptInvite(pendingInviteToken)
        localStorage.removeItem('pendingInviteToken')
      } catch (inviteError) {
        logger.error(
          '[Login] Error accepting invitation after password login:',
          inviteError,
        )
      }
    }

    if (await redirectToAdminReturnTarget(adminReturnTarget)) {
      return
    }

    if (isAddAccountFlow) {
      window.location.href = '/today?auth_success=true'
      return
    }

    const targetUrl = redirectUrl
      ? decodeURIComponent(redirectUrl)
      : adminReturnTarget
        ? '/organizations'
        : '/today'
    navigate(targetUrl)
  }

  const handlePasswordSubmit = async () => {
    const normalizedEmail = normalizeEmail(email)

    if (!normalizedEmail) {
      toast.error('Please enter your email')
      return
    }

    if (!password) {
      toast.error('Please enter your password')
      return
    }

    try {
      setIsLoading(true)
      const platform = isTauri()
        ? 'desktop'
        : isMobileAuthFlow()
          ? 'mobile'
          : undefined
      const response = await authApi.passwordLogin(
        normalizedEmail,
        password,
        platform,
      )

      if (response.success) {
        if (isMobileAuthFlow() && response.tokens) {
          const redirected = redirectToMobileApp({
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            expiresIn: response.tokens.expiresIn,
          })

          if (redirected) {
            return
          }
        }

        await completePasswordLogin()
        return
      }

      toast.error(response.message || 'Unable to log in with that password')
    } catch (error) {
      logger.error('[Login] Password login failed:', error)
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Unable to log in with that password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (authMode === 'password') {
      await handlePasswordSubmit()
      return
    }

    const normalizedEmail = normalizeEmail(email)

    if (!normalizedEmail) {
      toast.error('Please enter your email')
      return
    }

    try {
      setIsLoading(true)

      const response = await authApi.login(normalizedEmail)

      if (response.success && response.data) {
        const session =
          'session' in response.data ? response.data.session : null
        if (session) {
          // Use the Zustand store to set the login session with redirect URL
          setLoginSession(
            normalizedEmail,
            String(session),
            redirectUrl || undefined,
          )

          // Navigate to confirmation with redirect parameter preserved
          const confirmationParams = new URLSearchParams()
          if (redirectUrl) {
            confirmationParams.set('redirect', redirectUrl)
          }
          if (adminReturnTarget) {
            confirmationParams.set('adminReturn', adminReturnTarget)
          }
          if (isAddAccountFlow) {
            confirmationParams.set('addAccount', 'true')
          }
          const confirmationUrl = confirmationParams.toString()
            ? `/login/confirmation?${confirmationParams.toString()}`
            : '/login/confirmation'
          navigate(confirmationUrl)
          return
        }
      } else if (!response.success) {
        // Handle specific error messages from the API
        const message = response.message?.toLowerCase() || ''

        // Check if the response suggests registration
        if (
          response.data &&
          'suggestRegistration' in response.data &&
          response.data.suggestRegistration
        ) {
          showSignUpToast(normalizedEmail, isAddAccountFlow)
          return
        } else if (
          message.includes('no account') ||
          message.includes('not found') ||
          message.includes('not registered')
        ) {
          showSignUpToast(normalizedEmail, isAddAccountFlow)
          return
        } else {
          toast.error(response.message || 'An error occurred')
        }
        return
      }

      toast.error(response.message || 'Failed to start login process')
    } catch (error) {
      logger.error('[Login] Login failed:', error)

      // Check for specific error messages
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase()

        // Handle specific error cases
        if (
          errorMessage.includes('user not found') ||
          errorMessage.includes('no account') ||
          errorMessage.includes('not registered')
        ) {
          // Show sign up toast directly without additional API call
          showSignUpToast(normalizedEmail, isAddAccountFlow)
          return
        } else if (errorMessage.includes('verify your email')) {
          toast.error('Please verify your email before logging in.')
        } else if (
          errorMessage.includes('login failed') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('401')
        ) {
          toast.error(
            'Unable to log in. You can sign in with any connected method (Google, Microsoft, or email), or reset your password to enable email sign-in.',
          )
        } else {
          toast.error(error.message)
        }
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailAuthReveal = () => {
    setShowEmailAuth(true)
  }

  const isRedirectingAuthenticatedUser =
    isAuthenticated && !isMobileAuthFlow() && !isAddAccountFlow

  // Show loading state while checking auth or while the authenticated-user
  // redirect effect runs, so the login form never paints for signed-in users.
  if (authLoading || isRedirectingAuthenticatedUser) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center">
        <AppLoader inline size="md" />
        <span className="mt-4 text-base font-medium text-foreground/80">
          Loading...
        </span>
      </div>
    )
  }

  // Returning user: only greet "Welcome back." when we actually have a saved
  // account on this device — otherwise a first-time visitor would see it too.
  const isReturningUser =
    isTauri() && !isAddAccountFlow && visibleDesktopAccounts.length > 0

  // Custom headline: invite flow takes precedence, then returning-user greeting.
  const customHeadline = inviteInfo?.organizationName
    ? `Join ${inviteInfo.organizationName}`
    : isReturningUser
      ? 'Welcome back.'
      : undefined
  const customSubheadline = inviteInfo?.email
    ? `Sign in with ${inviteInfo.email} to accept your invitation.`
    : isReturningUser
      ? "Ready to crack the chaos? Let's get you signed in and back to what matters most."
      : undefined

  return (
    <SplitAuthLayout
      pageType="login"
      customHeadline={customHeadline}
      customSubheadline={customSubheadline}
    >
      <div className="w-full">
        {isTauri() &&
          !isAddAccountFlow &&
          (isLoadingAccounts || visibleDesktopAccounts.length > 0) && (
            <div className="mb-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Saved Accounts
              </p>
              {isLoadingAccounts ? (
                <p className="text-xs text-muted-foreground">
                  Loading saved accounts...
                </p>
              ) : (
                <div className="space-y-2">
                  {visibleDesktopAccounts.map(account => {
                    const accountLabel =
                      account.displayName ||
                      [account.firstName, account.lastName]
                        .filter(Boolean)
                        .join(' ') ||
                      account.email ||
                      'Account'
                    const isSelected = selectedDesktopAccountId === account.id
                    return (
                      <Button
                        key={account.id}
                        type="button"
                        variant="outline"
                        className="h-auto w-full justify-start gap-3 py-2"
                        onClick={() => handleSavedAccountSelect(account)}
                        disabled={Boolean(selectedDesktopAccountId)}
                      >
                        <UserAvatar
                          user={{
                            displayName: account.displayName || undefined,
                            firstName: account.firstName || undefined,
                            lastName: account.lastName || undefined,
                            email: account.email || undefined,
                          }}
                          size="sm"
                          className="h-6 w-6"
                        />
                        <div className="flex min-w-0 flex-col text-left">
                          <span className="truncate text-sm font-medium text-foreground">
                            {accountLabel}
                          </span>
                          {account.email && (
                            <span className="truncate text-xs text-muted-foreground">
                              {account.email}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <svg
                            className="ml-auto h-4 w-4 animate-spin text-muted-foreground"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                      </Button>
                    )
                  })}
                  {desktopAccountError && (
                    <div
                      role="alert"
                      className="rounded-xl border border-destructive/25 bg-destructive/5 p-3 text-sm text-foreground"
                    >
                      <p className="font-medium">Saved session expired</p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {desktopAccountError.message}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 rounded-lg"
                          onClick={() =>
                            handleDesktopAccountReauth(
                              visibleDesktopAccounts.find(
                                account =>
                                  account.id === desktopAccountError.accountId,
                              ),
                            )
                          }
                        >
                          Sign in again
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-lg"
                          onClick={() =>
                            handleDesktopAccountRemove(
                              desktopAccountError.accountId,
                            )
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        <div className="space-y-3">
          {!showEmailAuth && (
            <Button
              type="button"
              className="h-12 w-full justify-center gap-3 rounded-xl font-medium shadow-sm"
              onClick={handleEmailAuthReveal}
              disabled={isLoading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
              </svg>
              Continue with Email
            </Button>
          )}

          {showEmailAuth && (
            <div className="rounded-2xl border border-border/80 bg-background/95 p-3 shadow-[0_12px_36px_-28px_rgba(15,23,42,0.55)]">
              <div
                role="tablist"
                aria-label="Email sign-in method"
                className="relative mb-4 grid grid-cols-2 rounded-full border border-border bg-muted/40 p-1"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-full bg-background shadow-[0_2px_8px_-2px_rgba(15,23,42,0.10)] ring-1 ring-foreground/[0.04] transition-transform duration-300 ease-out"
                  style={{
                    transform:
                      authMode === 'password'
                        ? 'translateX(calc(100% + 0px))'
                        : 'translateX(0)',
                  }}
                />
                <button
                  type="button"
                  role="tab"
                  aria-selected={authMode === 'code'}
                  className={`relative z-10 h-9 rounded-full text-sm font-medium transition-colors disabled:opacity-60 ${
                    authMode === 'code'
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setAuthMode('code')}
                  disabled={isLoading}
                >
                  Email Code
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={authMode === 'password'}
                  className={`relative z-10 h-9 rounded-full text-sm font-medium transition-colors disabled:opacity-60 ${
                    authMode === 'password'
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setAuthMode('password')}
                  disabled={isLoading}
                >
                  Password
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-foreground"
                  >
                    Email
                  </Label>
                  <div className="relative">
                    <Input
                      ref={emailInputRef}
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(normalizeEmail(e.target.value))}
                      className="h-11 border-input bg-background pl-10"
                      disabled={isLoading}
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                      </svg>
                    </div>
                    {email && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        animation="none"
                        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={() => setEmail('')}
                        aria-label="Clear email"
                        disabled={isLoading}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </Button>
                    )}
                  </div>
                </div>

                {authMode === 'password' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label
                        htmlFor="password"
                        className="text-sm font-medium text-foreground"
                      >
                        Password
                      </Label>
                      <Link
                        to={
                          email
                            ? `/forgot-password?email=${encodeURIComponent(normalizeEmail(email))}`
                            : '/forgot-password'
                        }
                        className="text-xs font-medium text-primary hover:text-primary/80"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-11 border-input bg-background"
                      disabled={isLoading}
                      required={authMode === 'password'}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="h-11 w-full rounded-xl font-medium"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {authMode === 'password'
                        ? 'Signing in...'
                        : 'Sending code...'}
                    </span>
                  ) : authMode === 'password' ? (
                    'Sign in with Password'
                  ) : (
                    'Send Email Code'
                  )}
                </Button>
              </form>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full justify-center gap-3 rounded-xl border-input bg-background text-foreground hover:bg-muted"
            onClick={() => handleGoogleSignIn()}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Connecting to Google...
              </>
            ) : (
              <>
                <GoogleIcon className="h-4 w-4" />
                Continue with Google
              </>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-12 w-full justify-center gap-3 rounded-xl border-input bg-background text-foreground hover:bg-muted"
                disabled={isMicrosoftLoading}
              >
                {isMicrosoftLoading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Connecting to Microsoft...
                  </>
                ) : (
                  <>
                    <MicrosoftIcon className="h-4 w-4" />
                    Continue with Microsoft
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              className="w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              <DropdownMenuItem onSelect={() => handleMicrosoftSignIn('work')}>
                Work or school account
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleMicrosoftSignIn('personal')}
              >
                Personal account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Sign up link */}
        <div className="mt-8 flex justify-center">
          <p className="text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link
              to={(() => {
                const params = new URLSearchParams()
                if (email) params.set('email', normalizeEmail(email))
                if (isAddAccountFlow) params.set('addAccount', 'true')
                const queryString = params.toString()
                return `/register${queryString ? `?${queryString}` : ''}`
              })()}
              className="font-medium text-primary transition-colors hover:text-primary/80"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </SplitAuthLayout>
  )
}
